import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2, Plug, RefreshCw, XCircle, X, Inbox } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button, ButtonLink } from "./button";
import { Modal } from "./overlay";
import { ApiError, errorMessage } from "@/lib/api";

/** Errors a retry cannot fix: the platform account is missing, expired, or a demo account. */
const CONNECTION_CODES = new Set(["not_connected", "connection_expired", "demo_connection"]);

// ---------------------------------------------------------------------------------------------------------------
// Toasts

type ToastTone = "success" | "error" | "info" | "warning";
interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastApi {
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  /** Shows the API error message as an error toast. */
  fromError: (err: unknown, fallbackTitle?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const remove = useCallback((id: number) => setItems((all) => all.filter((t) => t.id !== id)), []);
  const push = useCallback(
    (tone: ToastTone, title: string, description?: string) => {
      const id = nextId.current++;
      setItems((all) => [...all.slice(-3), { id, tone, title, description }]);
      setTimeout(() => remove(id), tone === "error" ? 6500 : 4000);
    },
    [remove],
  );
  const api = useMemo<ToastApi>(
    () => ({
      success: (t, d) => push("success", t, d),
      error: (t, d) => push("error", t, d),
      info: (t, d) => push("info", t, d),
      warning: (t, d) => push("warning", t, d),
      fromError: (err, fallback = "Action impossible") => push("error", fallback, errorMessage(err)),
    }),
    [push],
  );
  const icons = { success: CheckCircle2, error: XCircle, info: Info, warning: AlertTriangle };
  const colors = { success: "text-success", error: "text-danger", info: "text-primary-400", warning: "text-warning" };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-3 bottom-3 z-[100] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:items-end" aria-live="polite">
        {items.map((t) => {
          const Icon = icons[t.tone];
          return (
            <div
              key={t.id}
              role={t.tone === "error" ? "alert" : "status"}
              className="pointer-events-auto flex w-full max-w-sm animate-pop-in items-start gap-3 rounded-xl border border-line-strong bg-[#0f1a33] p-3.5 shadow-pop"
            >
              <Icon className={cn("mt-0.5 size-5 shrink-0", colors[t.tone])} />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium text-ink">{t.title}</p>
                {t.description ? <p className="mt-0.5 text-[12.5px] text-muted">{t.description}</p> : null}
              </div>
              <button type="button" onClick={() => remove(t.id)} className="rounded p-0.5 text-muted hover:text-ink" aria-label="Fermer">
                <X className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast outside ToastProvider");
  return ctx;
}

// ---------------------------------------------------------------------------------------------------------------
// Confirm dialog

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback((opts: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ ...opts, resolve })), []);
  const close = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={Boolean(state)}
        onClose={() => close(false)}
        size="sm"
        title={state?.title}
        footer={
          <>
            <Button variant="secondary" onClick={() => close(false)}>
              {state?.cancelLabel ?? "Annuler"}
            </Button>
            <Button variant={state?.danger ? "danger" : "primary"} onClick={() => close(true)} autoFocus>
              {state?.confirmLabel ?? "Confirmer"}
            </Button>
          </>
        }
      >
        <div className="text-[13.5px] leading-relaxed text-ink-2">{state?.description}</div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm outside ConfirmProvider");
  return ctx;
}

// ---------------------------------------------------------------------------------------------------------------
// States

export function Spinner({ className, label = "Chargement" }: { className?: string; label?: string }) {
  return <Loader2 className={cn("size-5 animate-spin text-primary-400", className)} aria-label={label} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton rounded-lg", className)} aria-hidden />;
}

export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("space-y-2.5", className)}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  compact,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center text-center", compact ? "px-4 py-8" : "px-6 py-14", className)}>
      <div className={cn("flex items-center justify-center rounded-2xl border border-line-strong bg-panel-2 text-primary-400", compact ? "mb-3 size-11" : "mb-4 size-14")}>
        {icon ?? <Inbox className="size-6" />}
      </div>
      <p className="text-[14.5px] font-semibold text-ink">{title}</p>
      {description ? <p className="mt-1.5 max-w-md text-[13px] leading-relaxed text-muted">{description}</p> : null}
      {action ? <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry, className, title = "Chargement impossible" }: { error: unknown; onRetry?: () => void; className?: string; title?: string }) {
  if (error instanceof ApiError && CONNECTION_CODES.has(error.code)) {
    const demo = error.code === "demo_connection";
    return (
      <div role="status" className={cn("flex flex-col items-center justify-center px-6 py-12 text-center", className)}>
        <div className="mb-4 flex size-12 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10 text-primary-300">
          <Plug className="size-5.5" />
        </div>
        <p className="text-[14px] font-semibold">{demo ? "Disponible avec votre propre compte" : "Connexion nécessaire"}</p>
        <p className="mt-1 max-w-md text-[13px] text-muted">{error.message}</p>
        <ButtonLink to="/integrations" variant={demo ? "secondary" : "primary"} size="sm" className="mt-4" leftIcon={<Plug className="size-3.5" />}>
          Ouvrir les intégrations
        </ButtonLink>
      </div>
    );
  }
  return (
    <div role="alert" className={cn("flex flex-col items-center justify-center px-6 py-12 text-center", className)}>
      <div className="mb-4 flex size-12 items-center justify-center rounded-2xl border border-danger/30 bg-danger/10 text-danger">
        <AlertTriangle className="size-5.5" />
      </div>
      <p className="text-[14px] font-semibold">{title}</p>
      <p className="mt-1 max-w-md text-[13px] text-muted">{errorMessage(error)}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" className="mt-4" leftIcon={<RefreshCw className="size-3.5" />} onClick={onRetry}>
          Réessayer
        </Button>
      ) : null}
    </div>
  );
}

export function ProgressBar({ value, max = 100, tone = "blue", className }: { value: number; max?: number; tone?: "blue" | "green" | "orange" | "red" | "violet"; className?: string }) {
  const pct = Math.max(0, Math.min(100, max ? (value / max) * 100 : 0));
  const colors = { blue: "bg-primary", green: "bg-success", orange: "bg-warning", red: "bg-danger", violet: "bg-violet" };
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-[#1a2643]", className)} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn("h-full rounded-full transition-[width] duration-500", colors[tone])} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function InlineAlert({ tone = "info", title, children, className, action }: { tone?: "info" | "warning" | "error" | "success"; title?: string; children?: ReactNode; className?: string; action?: ReactNode }) {
  const styles = {
    info: "border-primary/30 bg-primary/[0.08] text-primary-300",
    warning: "border-warning/30 bg-warning/[0.08] text-[#fbbf24]",
    error: "border-danger/30 bg-danger/[0.08] text-[#f87171]",
    success: "border-success/30 bg-success/[0.08] text-[#4ade80]",
  };
  const Icon = tone === "success" ? CheckCircle2 : tone === "error" ? XCircle : tone === "warning" ? AlertTriangle : Info;
  return (
    <div className={cn("flex items-start gap-3 rounded-xl border px-3.5 py-3", styles[tone], className)}>
      <Icon className="mt-0.5 size-4.5 shrink-0" />
      <div className="min-w-0 flex-1 text-[13px]">
        {title ? <p className="font-medium text-ink">{title}</p> : null}
        {children ? <div className={cn("text-ink-2", title && "mt-0.5")}>{children}</div> : null}
      </div>
      {action}
    </div>
  );
}
